import { Flex, LoadingIcon } from "basis";
import { Account } from "library/accounts/types";
import { useAccountInfo } from "library/hooks/useAccountInfo";
import { useLayout } from "library/hooks/useLayout";
import { FC, useEffect, useReducer } from "react";
import { Layout } from "../Layout/Layout";
import { AlreadyAtMinimumCreditLimit } from "./AlreadyAtMinimumCreditLimit";
import { CanReduce } from "./CanReduce";
import {
  ACCOUNT_INFO_LOADED,
  CREDIT_LIMIT_DECREASE_HATEOAS_KEY,
  ERROR_HEADING,
  HEADING,
} from "./constants";
import { CreditLimitForm } from "./CreditLimitForm";
import { Error } from "./Error";
import { ImportantInformation } from "./ImportantInformation";
import { OverCreditLimit } from "./OverCreditLimit";
import {
  initialReduceCreditLimitState,
  PAGES,
  reduceCreditLimitReducer,
} from "./reduceCreditLimitReducer";
import { Success } from "./Success";

const pageInfo = {
  [PAGES.LANDING]: {
    heading: HEADING,
  },
  [PAGES.CAN_REDUCE]: {
    heading: HEADING,
    screenComponent: CanReduce,
  },
  [PAGES.ALREADY_AT_MINIMUM_CREDIT_LIMIT]: {
    heading: HEADING,
    screenComponent: AlreadyAtMinimumCreditLimit,
  },
  [PAGES.OVER_CREDIT_LIMIT]: {
    heading: HEADING,
    screenComponent: OverCreditLimit,
  },
  [PAGES.FORM]: {
    heading: HEADING,
    screenComponent: CreditLimitForm,
  },
  [PAGES.SUCCESS]: {
    heading: HEADING,
    screenComponent: Success,
  },
  [PAGES.ERROR]: {
    heading: ERROR_HEADING,
    screenComponent: Error,
  },
};

function ReduceCreditLimitScreen({
  screenComponent: ScreenComponent,
  state,
  dispatch,
}): JSX.Element {
  const { scrollToTop } = useLayout();

  useEffect(() => {
    scrollToTop();
  }, [scrollToTop, ScreenComponent]);

  return <ScreenComponent state={state} dispatch={dispatch} />;
}

interface Props {
  location: {
    state?: {
      account: Account;
    };
  };
}

const ReduceCreditLimit: FC<Props> = ({ location }) => {
  const { account } = location.state || {};
  const accountInfo = useAccountInfo(account);
  const postReduceCreditLimitHref = account?.links.find(
    ({ rel }) => rel === CREDIT_LIMIT_DECREASE_HATEOAS_KEY,
  )?.href;

  const [state, dispatch] = useReducer(
    reduceCreditLimitReducer,
    initialReduceCreditLimitState,
  );

  const { page } = state;
  const { heading: pageHeading, screenComponent } = pageInfo[page];
  const showImportantInformation = page === PAGES.FORM;

  useEffect(() => {
    if (accountInfo !== null) {
      dispatch({
        type: ACCOUNT_INFO_LOADED,
        accountInfo,
        postReduceCreditLimitHref,
      });
    }
  }, [accountInfo, postReduceCreditLimitHref]);

  return (
    <Layout>
      <Layout.Header>{pageHeading}</Layout.Header>
      <Layout.Main>
        {page === PAGES.LANDING ? (
          <Flex height="200" placeItems="center">
            <LoadingIcon size="medium" />
          </Flex>
        ) : (
          <ReduceCreditLimitScreen
            screenComponent={screenComponent}
            state={state}
            dispatch={dispatch}
          />
        )}
      </Layout.Main>
      {showImportantInformation && (
        <Layout.Sidebar>
          <ImportantInformation />
        </Layout.Sidebar>
      )}
    </Layout>
  );
};

export { ReduceCreditLimit };
